"use client"

import { useEffect, useLayoutEffect, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { Loader2 } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { EmployerPlanComparison } from "@/components/employer/employer-plan-comparison"
import { SettingsPageShell } from "@/components/settings/settings-page-shell"
import { useEmployerOrganisationOptional } from "@/context/employer-organisation-context"
import { managedEmployerOrgSlugStorageKey } from "@/constants"
import {
  EMPLOYER_ORGANISATION_SETTINGS_BILLING_SEGMENT,
  getEmployerOrganisationSettingsSection,
} from "@/constants/employer-organisation-settings-nav"
import {
  getUpgradePlanConfig,
  resolveUpgradePlanPath,
} from "@/constants/upgrade-plan"
import { useManagedOrganisationDetail } from "@/hooks/use-organisations"
import {
  useOrganisationBilling,
  useOrganisationCheckout,
  useOrganisationPortal,
  usePlanCatalog,
} from "@/hooks/use-plans"
import { setAuthReturnTo } from "@/lib/auth-return"

type EmployerOrganisationBillingSettingsPageProps = {
  slug?: string
}

export function EmployerOrganisationBillingSettingsPage({
  slug: slugProp,
}: EmployerOrganisationBillingSettingsPageProps) {
  const { slug: slugParam } = useParams<{ slug: string }>()
  const routeSlug = slugProp?.trim() || slugParam?.trim() || ""
  const upgradeConfig = getUpgradePlanConfig()
  const ctx = useEmployerOrganisationOptional()
  const billingSection = getEmployerOrganisationSettingsSection(
    EMPLOYER_ORGANISATION_SETTINGS_BILLING_SEGMENT,
  )

  const { data: organisation } = useManagedOrganisationDetail(routeSlug || undefined)
  const organisationName = organisation?.name ?? routeSlug ?? "this organisation"

  const [searchParams, setSearchParams] = useSearchParams()
  const checkoutStatus = searchParams.get("checkout")
  const shouldSync = checkoutStatus === "success"
  const [syncingAfterCheckout, setSyncingAfterCheckout] = useState(shouldSync)

  const { data: catalog } = usePlanCatalog()
  const { data: billing, isLoading, error, refetch, isFetching } = useOrganisationBilling(
    routeSlug || undefined,
    { sync: shouldSync },
  )
  const checkout = useOrganisationCheckout(routeSlug || undefined)
  const portal = useOrganisationPortal(routeSlug || undefined)

  useLayoutEffect(() => {
    if (!routeSlug || !ctx?.enabled) return
    try {
      window.localStorage.setItem(managedEmployerOrgSlugStorageKey(), routeSlug)
    } catch {
      /* ignore */
    }
    ctx.setActiveOrganisationSlug(routeSlug)
  }, [routeSlug, ctx])

  useEffect(() => {
    if (checkoutStatus === "success") {
      setSyncingAfterCheckout(true)
      void refetch().finally(() => setSyncingAfterCheckout(false))
    }
  }, [checkoutStatus, refetch])

  const clearCheckoutParam = () => {
    if (!checkoutStatus) return
    const next = new URLSearchParams(searchParams)
    next.delete("checkout")
    setSearchParams(next, { replace: true })
  }

  const billingReturnPath = resolveUpgradePlanPath(routeSlug)

  const handleUpgrade = async () => {
    const targetPlanId = billing?.upgrade_target?.plan_id
    if (!targetPlanId || !routeSlug) return
    const origin = window.location.origin
    const successUrl = `${origin}${billingReturnPath}?checkout=success`
    setAuthReturnTo(`${billingReturnPath}?checkout=success`)
    const result = await checkout.mutateAsync({
      planId: targetPlanId,
      successUrl,
      cancelUrl: `${origin}${billingReturnPath}?checkout=cancelled`,
    })
    if (result.checkout_url) {
      window.location.assign(result.checkout_url)
    }
  }

  const handlePortal = async () => {
    const result = await portal.mutateAsync()
    if (result.portal_url) {
      window.location.assign(result.portal_url)
    }
  }

  const title = billingSection?.label ?? upgradeConfig?.label ?? "Plan & Billing"
  const description =
    billingSection?.description ??
    "View your subscription, upgrade your plan, and manage billing for this organisation."

  const canUpgrade = Boolean(catalog?.enabled && billing?.upgrade_target)
  const headerActions =
    billing?.subscription_status || billing?.can_manage_billing ? (
      <div className="flex flex-wrap items-center justify-end gap-4">
        {billing.subscription_status ? (
          <p className="text-sm capitalize text-muted-foreground">
            Subscription: {billing.subscription_status.replace(/_/g, " ")}
          </p>
        ) : null}
        {billing.can_manage_billing ? (
          <Button
            variant="outline"
            onClick={() => void handlePortal()}
            disabled={portal.isPending}
          >
            {portal.isPending ? <Loader2 className="animate-spin" aria-hidden /> : null}
            Manage billing
          </Button>
        ) : null}
      </div>
    ) : null

  return (
    <SettingsPageShell
      title={title}
      description={description}
      layout="wide"
      actions={headerActions}
    >
        {checkoutStatus === "success" ? (
          <Card>
            <CardHeader>
              <CardTitle>Subscription updated</CardTitle>
              <CardDescription>
                Your checkout completed. Plan details may take a moment to sync from Stripe.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" size="sm" onClick={clearCheckoutParam}>
                Dismiss
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {checkoutStatus === "cancelled" ? (
          <Card>
            <CardHeader>
              <CardTitle>Checkout cancelled</CardTitle>
              <CardDescription>You can upgrade again whenever you are ready.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" size="sm" onClick={clearCheckoutParam}>
                Dismiss
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {!routeSlug ? (
          <Card>
            <CardHeader>
              <CardTitle>No organisation selected</CardTitle>
              <CardDescription>
                Choose a workspace from the sidebar switcher to view billing.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : isLoading || syncingAfterCheckout || (shouldSync && isFetching) ? (
          <Card>
            <CardContent className="flex items-center gap-2 py-8 text-sm text-muted-foreground" role="status">
              <Loader2 className="size-4 animate-spin" />
              {syncingAfterCheckout || shouldSync
                ? `Syncing subscription for ${organisationName}…`
                : `Loading billing for ${organisationName}…`}
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardHeader>
              <CardTitle>Unable to load billing</CardTitle>
              <CardDescription>
                {(error as Error).message || "Try again in a moment."}
              </CardDescription>
            </CardHeader>
          </Card>
        ) : billing ? (
          <Card>
              <CardHeader>
                <CardTitle>Plans</CardTitle>
              </CardHeader>
              <CardContent>
                <EmployerPlanComparison
                  currentPlanId={billing.plan_id}
                  canUpgrade={canUpgrade}
                  upgrading={checkout.isPending}
                  onUpgrade={() => void handleUpgrade()}
                />
              </CardContent>
            </Card>
        ) : null}
    </SettingsPageShell>
  )
}
